/**
 * ContactEditModal file-local pure utilities.
 * Extracted verbatim from ContactEditModal.tsx (PR5 Phase 2).
 * `formatNoteWithStyledLabels` returns JSX → this file is .tsx.
 * `formatNumberWithCommas` keeps the (value: string) signature — do NOT
 * merge with ContactListModule's number variant.
 */
import type React from 'react';

export function stripHtmlToText(html: string): string {
  if (!html) return '';
  // Skip Google Calendar invites entirely
  if (html.includes('calendar.google.com') || html.includes('Join with Google Meet') || html.includes('schema.org/Event')) {
    return '[Calendar Invite]';
  }
  // Strip HTML tags
  let text = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    // Decode numeric HTML entities (&#NNN; and &#xHHH;)
    .replace(/&#x([0-9a-fA-F]+);/g, (_m, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_m, dec) => String.fromCharCode(parseInt(dec, 10)))
    // Strip invisible/zero-width Unicode characters (tracking pixels, spacer hacks)
    // eslint-disable-next-line no-misleading-character-class
    .replace(/[\u00AD\u034F\u061C\u070F\u180E\u200B-\u200F\u2028-\u202F\u2060-\u206F\u2800\uFEFF\uFFF9-\uFFFB]/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"');
  // Strip quoted replies — truncate at the EARLIEST quote marker so we
  // keep only the user's own reply, not the embedded prior thread.
  const quotePatterns = [
    /[\n\r]On .{10,120} wrote:/,
    / On .{10,120} wrote:/,
    /[\n\r]>.*$/m,
    /[\n\r]_{10,}/,
    /[\n\r]CONFIDENTIALITY NOTICE:/,
    /[\n\r]EXTERNAL EMAIL/,
    // Generic disclaimer leader: "Note: The information contained..."
    /[\n\r]Note:\s+The (information|content|message)/i,
    /[\n\r]This (e-?mail|message) (and any|may contain|is intended)/i,
    /[\n\r]DISCLAIMER:/i,
    /[\n\r]-{2,}\s*[\n\r]/,
    // Outlook-style quoted reply header. The lines may be separated by
    // blank lines (Outlook for iOS/Mac inserts them), so we accept any
    // intervening whitespace between From: and Sent:.
    /[\n\r]From:[\s\S]{0,400}?[\n\r]\s*Sent:/i,
    // Single-line "From: <email>" — minimal Outlook fallback when Sent:
    // line was stripped by the client (mobile Outlook sometimes does this).
    /[\n\r]From:\s.{1,200}@/i,
    // Outlook for iOS / Spark / native mail-app quote separator
    /[\n\r]Outlook for (iOS|Mac|Android)/i,
    /[\n\r]-+\s*Original Message\s*-+/i,
    /[\n\r]Begin forwarded message:/i,
  ];
  let earliestQuote = -1;
  for (const pat of quotePatterns) {
    const match = text.search(pat);
    if (match > 5 && (earliestQuote === -1 || match < earliestQuote)) {
      earliestQuote = match;
    }
  }
  if (earliestQuote > 5) text = text.substring(0, earliestQuote);
  // Strip mobile-mail signatures
  const sigPatterns = [
    /\nBest regards[\s\S]*$/i,
    /\n--\s*\n[\s\S]*$/,
    /\nSent from [\s\S]*$/i,
    /\nSent with Spark[\s\S]*$/i,
    /\nGet Outlook for [\s\S]*$/i,
    /\nBest,[\s\S]*$/i,
  ];
  for (const pat of sigPatterns) {
    text = text.replace(pat, '');
  }
  // Strip a trailing signature block: a name/title line followed by
  // multi-line contact info (Office/Phone/Address). This catches the
  // common "Pamela Van Laningham, PT, CHT\nSelect Physical Therapy..."
  // shape that's not preceded by a "Sent from" or "--" delimiter.
  // Approach: split into lines, walk backward from the end, count
  // consecutive "signature-shaped" lines; if there's a strong signal
  // (≥2 of: phone, address keyword, credential after comma) within
  // those lines, cut from the first sig-shaped line onward.
  {
    const lines = text.split(/\r?\n/);
    const isSigLine = (raw: string): boolean => {
      const line = raw.trim();
      if (line.length === 0) return true; // blank lines stay inside the block
      if (line.length > 100) return false; // long lines are usually prose
      // Sentence-ending punctuation suggests prose, not a sig.
      // (Allow trailing comma/period after credentials like "PT, CHT.")
      if (/[.!?]\s+\S/.test(line)) return false;
      // Hallmarks of signature lines:
      const hallmark =
        /^(Office|Phone|Tel|Fax|Mobile|Cell|Direct|P|O|M|E|W|Email|Web)\s*:/i.test(line) ||
        /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/.test(line) || // phone
        /\bSuite\b|\bSte\.?\b|\bFloor\b|\bUnit\b|\bAve\b|\bStreet\b|\bBlvd\b|\bRoad\b|\bRd\b/i.test(line) ||
        /\b\d{5}(?:-\d{4})?\b/.test(line) || // ZIP
        /,\s*(PT|MD|JD|MBA|PhD|CFA|CFP|CHT|Esq\.?|CPA|RN|DO|DDS|LCSW|PE|MSW|RD|MS|BS|BA)\b/.test(line) ||
        /^[A-Z][a-z]+(?: [A-Z][a-z'-]+){1,3}\s*,\s*[A-Z]/.test(line) || // "First Last, Cred"
        /^[A-Z][a-z]+(?: [A-Z][a-z'-]+){1,3}$/.test(line); // bare "First Last" name
      return hallmark;
    };
    // Looser predicate used once we're already inside the sig block —
    // must still LOOK signature-shaped, just tolerant of any character
    // set. Requires at least one strong signal (multi-capitalized line,
    // digits, or a contact-info keyword) so that closings like "See you
    // at your next session" (no period, capital first) don't sneak in.
    const isSigLikeLine = (raw: string): boolean => {
      const line = raw.trim();
      if (line.length === 0) return true;
      if (line.length > 100) return false;
      if (/[.!?]\s*$/.test(line)) return false;
      if (/[.!?]\s+[A-Z]/.test(line)) return false;
      if (/^[a-z]/.test(line)) return false;
      const capWords = (line.match(/\b[A-Z][a-z'-]+/g) || []).length;
      const allCapsAcronym = /\b[A-Z]{2,}\b/.test(line); // PT, CHT, MBA, etc.
      const hasMultipleCaps = capWords >= 2 || (capWords >= 1 && allCapsAcronym);
      const hasDigit = /\d/.test(line);
      const hasContactKeyword = /\b(Office|Phone|Tel|Fax|Mobile|Cell|Direct|Email|Web|Suite|Ste\.?|Floor|Avenue|Ave|Street|St\.|Boulevard|Blvd|Road|Rd\.|www\.|\.com|\.org|\.net|@\w)\b/i.test(line);
      if (!hasMultipleCaps && !hasDigit && !hasContactKeyword) return false;
      return true;
    };
    let blockStart = lines.length;
    let consecutive = 0;
    let inBlock = false;
    for (let i = lines.length - 1; i >= 0; i--) {
      const looserOk = inBlock ? isSigLikeLine(lines[i]) : isSigLine(lines[i]);
      if (looserOk) {
        blockStart = i;
        consecutive++;
        inBlock = true;
        continue;
      }
      break;
    }
    if (consecutive >= 3) {
      // Require hits from DISTINCT lines so a single line like
      // "Phone: 555-1234" (which scores both phone + address-keyword on
      // its own) can't single-handedly authorize truncation. Real
      // signatures spread their signals across name/firm/address/contact
      // lines; a one-line phone in an otherwise-prose message shouldn't.
      const phoneRe = /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/;
      const addressRe = /\b(Suite|Ste\.?|Office:|Tel:|Phone:|Fax:|www\.|\b\d{5}(?:-\d{4})?\b|\bAve\b|\bBlvd\b)/i;
      const titleRe = /,\s*(PT|MD|JD|MBA|PhD|CFA|CFP|CHT|Esq\.?|CPA|RN|DO|DDS|LCSW|PE)\b/;
      const linesWithPhone = new Set<number>();
      const linesWithAddress = new Set<number>();
      const linesWithTitle = new Set<number>();
      for (let i = blockStart; i < lines.length; i++) {
        const line = lines[i];
        if (phoneRe.test(line)) linesWithPhone.add(i);
        if (addressRe.test(line)) linesWithAddress.add(i);
        if (titleRe.test(line)) linesWithTitle.add(i);
      }
      // Count distinct lines that contributed any signal.
      const distinctSignalLines = new Set<number>([
        ...linesWithPhone, ...linesWithAddress, ...linesWithTitle,
      ]);
      // Need ≥2 different signal *types* AND ≥2 different lines carrying
      // signals — protects against a single-line "Phone: 555-1234" message
      // ending while still firing on real signatures with name, address,
      // and contact lines.
      const signalTypes = [linesWithPhone.size > 0, linesWithAddress.size > 0, linesWithTitle.size > 0]
        .filter(Boolean).length;
      if (signalTypes >= 2 && distinctSignalLines.size >= 2) {
        text = lines.slice(0, blockStart).join('\n');
      }
    }
  }
  // Collapse whitespace — normalize blank lines (may contain spaces/tabs/nbsp)
  text = text.replace(/[ \t]+$/gm, '').replace(/(\n\s*){3,}/g, '\n\n').replace(/[ \t]+/g, ' ').trim();
  // Truncate
  if (text.length > 500) text = text.substring(0, 500) + '...';
  return text;
}

/** Style "Follow Up:", "Last Touch:", "Completed On:", and "Previous Touch:" text in blue (menu module color) */
export function formatNoteWithStyledLabels(note: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  // Match "Follow Up" (with or without colon), "Last Touch:", "Completed On:", or "Previous Touch:" at the start of lines
  const regex = /^(Follow Up:?|Last Touch:|Completed On:|Previous Touch:|Snoozed to:)/gm;
  let match;

  while ((match = regex.exec(note)) !== null) {
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;

    // Add text before the match
    if (matchStart > lastIndex) {
      parts.push(note.substring(lastIndex, matchStart));
    }

    // Add the styled label
    parts.push(
      <span key={matchStart} style={{ color: '#0369a1', fontWeight: 500 }}>
        {match[0]}
      </span>
    );

    lastIndex = matchEnd;
  }

  // Add remaining text after the last match
  if (lastIndex < note.length) {
    parts.push(note.substring(lastIndex));
  }

  return parts.length > 0 ? parts : note;
}

// Format number with commas (e.g., 200000 -> "200,000")
export function formatNumberWithCommas(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('en-US');
}

// Parse formatted string back to number (e.g., "200,000" -> 200000)
export function parseFormattedNumber(value: string): number {
  return parseInt(value.replace(/,/g, ''), 10) || 0;
}

export const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today at ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  }
};
